import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { WizDateUtils, WizMathUtils, WIZ_PHYSIC_POSITION } from '@wizeoo/akm-core-lib';
import { PopUpManager, WizIcon, WizPopUp, WizRoot, WizEventManager } from '@wizeoo/akm-component-lib';
import { CalendarOptions } from '@fullcalendar/core'; // l'import calendar est nécessaire, sinon ça plante
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { WidgetConfigurationItemObj} from '@wizeoo/akm-message-lib';
import { EM_ACTION, MathUtils } from '@wizeoo/akm-compute-lib';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ViewData } from '../../../model/view.data';
import { FreedataArgsVO } from '../../../vo/core/freedataargs.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { PopUtils } from '../../../framework/components/pop/pop-utils';
import { IWidget } from '../../../manager/widgets/i-widget';
import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { FastSelectData } from './../../../manager/widgets/widget.engine';
import { AlkEvent } from '../../../framework/events/alk.event';
import { FreedataWidgetBasic } from '../base/freedata-widget-basic';
import { DeleteFreedataValueParams, FreedataGridDeletePop } from '../freedatas/pops/freedata-grid-delete.pop';
import { FreedataFormulaCoreEngine } from '../../../manager/computer/freedata.formula.core.engine';
import { CompositeSourcePack } from '../../../command/core/sync/sync.service';
import { Color, colorSets } from '../../ngx-charts/utils/color-sets';
import { FreedataCalendarSettingsPop } from './pops/freedata-calendar-settings.pop';
import { calendarEditPopVO, FreedataCalendarAddPop } from './freedata-calendar-add.pop';
import { CalendarEventRenderer } from '../calendar/calendar-event.renderer';

@Component({
  selector: 'freedata-calendar-widget',
  templateUrl: './freedata-calendar.widget.html',
  styleUrls: ['./freedata-calendar.widget.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** affichage des données en mode calendaire */
export class FreedataCalendarWidget extends FreedataWidgetBasic implements AfterViewInit, OnDestroy {
  
  constructor( elementRef: ElementRef, private cdref: ChangeDetectorRef, protected componentFactoryResolver: ComponentFactoryResolver,
    protected appRef: ApplicationRef, protected injector: Injector
  ) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.FD_CALENDAR;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.FREEDATA;
    this.settingsPopClassFactory = FreedataCalendarSettingsPop;
  }
  
  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  public readyToGo = false; // le calendar est OK, sinon sale effet de refresh

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public events = []; // la collection d'events
  public scheme: Color = colorSets[4]; // jeu de couleur

  /**
   * injection du contenu Angular sur une entrée html
   * @param info les infos du renderer de base (html)
   */
  private doMount(info) {
    // console.log('info', info);
    const bodyPortalHost = new DomPortalOutlet(
      info.el, // Target the element where you wish to append your component
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );

    const newCompo = new ComponentPortal(CalendarEventRenderer); // renderer à injecter
    const refCompo = bodyPortalHost.attach(newCompo);
    refCompo.instance.data = info.event._def; // passage du data
  }

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    editable: true,
    firstDay: 1,
    events: this.events,
    eventDidMount: this.doMount.bind(this), // injection de composant dans les renderers
    eventResizableFromStart: true,
    // eventDragStart: this.evDragStart.bind(this),
    fixedMirrorParent: document.body,
    // windowResize
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth dayGridWeek',
    },
    datesSet: this.changeDate.bind(this),
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.handleEventClick.bind(this),
    eventDrop: this.alterDate.bind(this),
    eventResize: this.alterDate.bind(this),
    locales: allLocales, 
    locale: 'fr',
  };

  private startField: string; // champ de début de plage
  private endField: string; // champ de fin de plage
  private colorField: string; // champ pour la couleur


    /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */
  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // simple refresh sur recalcul

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // alimentation du level pour filtre rapide des slaves
      this.freedataCode = this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[0];
      this.level = Number(this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[1]);
      
      // construction de la FeiKeyDef unique du widget
      this.buildFeiKeyDef(this.vision, this.context);
    }
    
    if(origin === WIDGET_ACTION_ORIGIN.READY) { // je me suis dit tout seul que j'étais prêt
      mustBuildRows = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustRefresh = true;
    } 

    // modification du pool de sélection sur un master
    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) { // TODO TODO Faire moins bourrin
      mustBuildRows = true;
      mustRefresh = true;
    }


    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustRefresh) {
      this.fdvDS.refresh();
      this.buildCalendar();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
    if(this.state >= WIDGET_STATE.READY) { // je viens de passer à READY, j'enchaine
      this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné (pour déclencher les slaves)
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this));
    }
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.DELETE_CALENDAR_EVENT, this.onItemDeleted);
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.EDIT_CALENDAR_EVENT, this.doEdit);
    
    // WizEventManager.addListener(this, WizRoot.getInstance().elementRef, WIZ_EVENT_TYPE.TRANSLATION_LOADED, this.translationLoaded);
    
  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, this.elementRef, ALK_EVENT_TYPE.DELETE_CALENDAR_EVENT);
    WizEventManager.removeListener(this, this.elementRef, ALK_EVENT_TYPE.EDIT_CALENDAR_EVENT);

   // WizEventManager.removeListener(this, WizRoot.getInstance().elementRef, WIZ_EVENT_TYPE.TRANSLATION_LOADED);

  }

  /** construction graphique */
  private buildCalendar(): void {

    this.calendarOptions.locale = this.model.currentLocaleCode;
    this.calendarOptions.initialDate = this.getContextDate();
    
    if (this.context.c.length === 2 || this.context.c.length === 3) {
      this.startField = this.context.c[0].fdvf;
      this.endField = this.context.c[1].fdvf;
      if (this.context.c.length === 3) {
        this.colorField = this.context.c[2].fdvf;
      }
      // console.log('calendar draw', this.mainDS, this.context);
      this.generateEvents();
    } else {
      this.events = [];
    }

    setTimeout(() => {
      this.calendarOptions.events = this.events;
      this.readyToGo = true;
      this.cdref.markForCheck();
    }, 0);
  }

  /** construction des events calendar depuis mainDS */
  private generateEvents(): void {
    this.events = [];
    const domLen: number = this.scheme.domain.length; // prise du pool de couleurs

    this.fdvDS.getWorkedSource()?.forEach((fdv: FreedataValueVO) => {
      let evtColor: string = null;
      if (!!this.colorField) {
        let part: string = fdv.argsObjVO[this.colorField];
        const conf: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fdv.freeDataCode, this.colorField);
        if (!!conf) {
          evtColor = this.model.formatEngine.applyFormat(part, conf.dataType, conf.format, null);
        }
      }
      if (!evtColor) {
        evtColor = this.scheme.domain[this.events.length % domLen];
      }

      // alimentation de la couleur de contraste pour les rendus des libellés
      let contrast: string = MathUtils.invertColor(evtColor);


      const titleParts: string[] = [];
      this.context.r.forEach((wcio: WidgetConfigurationItemObj) => { // prise des éléments de titre
        let part: string = fdv.argsObjVO[wcio.fdvf];
        const conf: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fdv.freeDataCode, wcio.fdvf);
        if (!!conf) {
          part = this.model.formatEngine.applyFormat(part, conf.dataType, conf.format, null);
        }
        titleParts.push(part);
      });
      
      let start: string = fdv.argsObjVO[this.startField];
      let end: string = fdv.argsObjVO[this.endField];
      
      let datePart: string[] = [];
      datePart.push(WizDateUtils.numberToLocalizedString(Number(start?.substring(0,8))));
      datePart.push(WizDateUtils.numberToLocalizedString(Number(end?.substring(0,8))));


      
      this.events.push({
        editable: this.checkMe(fdv),
        title: titleParts.join(' - '),
        start: WizDateUtils.dateToString(WizDateUtils.stringToTime(start), 'yyyy-MM-dd'),
        end: WizDateUtils.dateToString(WizDateUtils.stringToTime(end), 'yyyy-MM-dd'),
        color: evtColor,
        extendedProps: {
          context: this.context,
          freedataValue: fdv,
          id: fdv.code,
          contrast: contrast,
          content: datePart.join(' - '),
          title: titleParts.join(' - '),
          ds: this.fdvDS,
        },
      });
    });
  }

  /* ##############################################################################
            Edition
     ############################################################################## */

  /** contrôle d'éligibilité au dnd */
  private checkMe(fdv: FreedataValueVO): boolean {
    // console.log('checkMe', fdv);
    const restrictSet: Set<string> = new Set();

    
    const fieldsMap = new Map( fdv.getParentFDVO().allFieldsAsArgs.map(item => [item.code, item]));

    [...fieldsMap.values()].forEach((fdavo: FreedataArgsVO) => { // pour chaque champ
      if (fdavo.code === this.startField || fdavo.code === this.endField) {
      // contrôle des restrictions
      if(!!fdavo.restrict) { // en cas de validateur
        const exps: string[] = fdavo.restrict.split(ViewData.STYLE_ELEMENT_SEPARATOR);
        let result: boolean = true;

        exps.forEach((exp: string) => {
          if (result && !this.model.freedataFormulaCoreEngine.checkFreedataExp(fdv, exp, this.fei)) {
            result = false;
          }
        })

        if (!result) {
          restrictSet.add(fdavo.code);
        }
      }
      }
    });

    if( restrictSet.size > 0) {
      return false;
    } else {
      return true;
    }
    
  }

  /** suppression depuis le renderer d'un event du calendar */
  private onItemDeleted(e: AlkEvent): void {
    let width: string = '400px';
    let height: string = '350px';

    const toDie: FreedataValueVO = e.data.freedataValue;
    let params: DeleteFreedataValueParams = {fdv: toDie, widget: this, callback: this.doDelete.bind(this)};


    const deletePop: WizPopUp = new WizPopUp(FreedataGridDeletePop, false, false, null, width, height, params);
    const pos: DOMRect =  (e.target as Element).getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, deletePop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    deletePop.customPosition = targetPosition;

    if (PopUpManager.exist(deletePop)) {
      // suppression de l'instance existante
      deletePop.hide();
    }
    // ouverture de la pop. Data[fei, context, WizbaseCollector, PivotField, callback]
    deletePop.show();
  }

  /** ajout d'un event sur clic sur une date */
  private handleDateClick(arg) {
    const startDate: number = WizDateUtils.dateToNumber(arg.date);
    this.showDatePop(null, startDate);
    let fsd: FastSelectData = {fei: this.fei, kind: this.widgetGlobalKind, data: null};
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.PIVOT_DOUBLE, fsd));
  }

  private doEdit(e: AlkEvent): void {
    this.showDatePop(e.data.freedataValue, null);
  }

  /**
   * @override IWidget : ouverture de la popUp de configuration (clic sur l'icone de conf ou nouvelle date)
   * @param fdv de l'event
   * @param startDate: la date du point d'impact
   */
  public showDatePop(fdv?: FreedataValueVO, startDate?: number): void {

    const fd: FreeDataVO = this.model.freedataEngine.getFreeData(this.freedataCode);
    const data: calendarEditPopVO = {widget: this, freedata: fd, freedataValue: fdv, context: this.context, startDate: startDate, callback: this.backFromAddPop.bind(this)};

    // ouverture de la pop. Data[fei, context, callback]
    new WizPopUp(FreedataCalendarAddPop, false, false, null, '500px', '500px', data).show();
  }

  // retour de demande d'ajout, ajout véritable
  private backFromAddPop(e: any): void {}


  /** resize ou dnd sur un event du calendar */
  private alterDate(arg) {
    // arg = event calendar
    const eventID: number = arg.event._def.extendedProps.id;
    const toUpdateEvent = new Map(this.events.map((e) => [e.extendedProps.id, e])).get(eventID);
    
    const fdv: FreedataValueVO = toUpdateEvent.extendedProps.freedataValue;
    const pair = fdv.trueArgsObj; // les props du FDV
    const oldStartDate = pair[this.startField];
    const oldEndDate = pair[this.endField];

    const startDate = WizDateUtils.dateToString(arg.event._instance.range.start);
    const endDate = WizDateUtils.dateToString(arg.event._instance.range.end);
    
    pair[this.startField] = startDate;
    pair[this.endField] = endDate;
    this.model.freedataEngine.feedFreedataValueField(fdv, this.startField, startDate);
    this.model.freedataEngine.feedFreedataValueField(fdv, this.endField, endDate);
    // fdv.argsObj = pair; // mise à jour du FDV

    // contrôle des valeurs
    const fieldsMap = new Map( fdv.getParentFDVO().allFieldsAsArgs.map(item => [item.code, item]));
    const errorSet: Map<string, string> = new Map();
    
    [...fieldsMap.values()].forEach((fdavo: FreedataArgsVO) => { // pour chaque champ
      if(!!fdavo.validator) { // en cas de validateur
        const exps: string[] = fdavo.validator.split(ViewData.STYLE_ELEMENT_SEPARATOR);
        let result: boolean = true;
  
        exps.forEach((exp: string) => {
          if (result && exp === FreedataFormulaCoreEngine.MANDATORY) {
            if(!fdv.argsObjVO[fdavo.code]) {
              result = false;
              // console.log('missing mandatory', this.data.arg.code);
            }
          } else if (result &&  !this.model.freedataFormulaCoreEngine.checkFreedataExp(fdv, exp, this.fei)) {
            result = false;
          }
        })

        if (!result) {
          errorSet.set(fdavo.code, fdavo.trsValueControlMessage);
        }
      }
    });

    if( errorSet.size > 0) {
      [...errorSet.values()].forEach(err => { // affichage des erreurs, 3 max.    
        WizPopUp.toast(err, WizIcon.TOAST_ERROR).show();
      })
      // WizPopUp.toast('$.common.error.$', WizIcon.TOAST_ERROR).show();
      arg.revert();
      pair[this.startField] = oldStartDate;
      pair[this.endField] = oldEndDate;
      this.model.freedataEngine.feedFreedataValueField(fdv, this.startField, oldStartDate);
      this.model.freedataEngine.feedFreedataValueField(fdv, this.endField, oldEndDate);
      // fdv.argsObj = pair; // mise à jour du FDV
    } else {
      const fd = this.model.freedataEngine.getFreeData(fdv.freeDataCode);
      fd.resetUpdateMap(); 
      // fd.updateMap = new Map<string, [FreedataValueVO, string]>(); // on rince la map, elle est peut-être "chargée"
      fd.getValuesVO(); // juste un hit, ça va construire
      // TODO TODO fdv.parentFDVO = fd; // on force
  
      fdv.getParentFDVO().maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
      fdv.getParentFDVO().addToUpdatePack(fdv, EM_ACTION.UPDATE);


      let fdMap: Map<string, FreeDataVO> = new Map();
      fdMap.set(fdv.getParentFDVO().code, fdv.getParentFDVO());

      const csp: CompositeSourcePack = {
        freedataManagement: true,
        freedata: fdMap, // update / create / delete sur FDV
         updates: null, // maj sur des Baselines / BLVs
      }

      // const csp: CompositeSourcePack = {
      //   freedata: fdv.getParentFDVO(), // update / create / delete sur FDV
      //   // newBornBaselines: null, // BLs à créer
      //   // toDieBaselines: null, // BLs à supprimer
      //   updates: null, // maj sur des Baselines / BLVs
      //   // callback: this.backFromAdd.bind(this) // de retour
      // }
  
      this.model.compositeEngine.compositeCommit(csp); // compositeMerge(csp);
    }
  }

  /** sélection d'un event */
  handleEventClick(arg) {
    // console.log(arg);
    const fdv: FreedataValueVO = arg.event._def.extendedProps.freedataValue;
    this.fdvDS.toggle(fdv);
    let fsd: FastSelectData = {fei: this.fei, kind: this.widgetGlobalKind, data: fdv};
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.PIVOT_DOUBLE, fsd));
  }

}
